import { Alert, Checkbox, Group, Stack, Text } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';

import { ScopeComponent } from '@/types';

/** Providers switched off per data-leaf slug. Absent slug == run them all. */
export type ExcludedProviders = Record<string, string[]>;

interface DataSourceProvidersProps {
  /** The API-fulfilled leaves this section depends on. */
  leaves: ScopeComponent[];
  /** Current exclusions, keyed by leaf slug. */
  excluded: ExcludedProviders;
  /**
   * True when the settings JSON can't be parsed — the control would otherwise
   * write into a document it can't read, silently discarding the other keys.
   */
  disabled?: boolean;
  /**
   * Leaf slug -> names of the *other* sections in this config that also use it.
   * A provider is only actually switched off when every section using the leaf
   * excludes it, so unchecking here has no effect while a sibling still wants
   * the data — which is worth saying rather than leaving to be discovered.
   */
  sharedWith?: Record<string, string[]>;
  onChange: (next: ExcludedProviders) => void;
}

/**
 * Per-section control over which providers fulfil its data.
 *
 * Every provider a leaf declares is checked by default: this stores the
 * *unchecked* ones, so a provider added to the leaf later shows up enabled here
 * without anyone editing the config. Selection can only narrow what the leaf
 * already declares — adding a provider is a change to the data component itself.
 */
export function DataSourceProviders({
  leaves,
  excluded,
  disabled = false,
  sharedWith = {},
  onChange,
}: Readonly<DataSourceProvidersProps>) {
  if (leaves.length === 0) {
    return null;
  }

  const handleChange = (slug: string, declared: string[], enabled: string[]) => {
    const off = declared.filter((p) => !enabled.includes(p));
    const next = { ...excluded };
    if (off.length > 0) {
      next[slug] = off;
    } else {
      // Absent rather than empty — an empty list means the same thing and
      // would just accumulate as noise in the stored settings.
      delete next[slug];
    }
    onChange(next);
  };

  return (
    <Stack gap="xs">
      <Stack gap={2}>
        <Text size="sm" fw={500}>
          Data sources
        </Text>
        <Text size="xs" c="dimmed">
          Unchecking a provider stops it being called for this section. Providers listed only for
          certain jurisdictions still appear here; they run only where they apply.
        </Text>
      </Stack>

      {disabled && (
        <Alert icon={<IconAlertCircle size={14} />} color="yellow" variant="light" p="xs">
          <Text size="xs">Fix the settings JSON below to change providers.</Text>
        </Alert>
      )}

      {leaves.map((leaf) => {
        const declared = leaf.providers ?? [];
        const off = excluded[leaf.slug] ?? [];
        const enabled = declared.filter((p) => !off.includes(p));
        const noneLeft = enabled.length === 0;
        const sharers = sharedWith[leaf.slug] ?? [];

        return (
          <Stack key={leaf.slug} gap={4}>
            <Checkbox.Group
              label={leaf.name}
              value={enabled}
              onChange={(next) => handleChange(leaf.slug, declared, next)}
            >
              <Group gap="md" mt={4}>
                {declared.map((provider) => (
                  <Checkbox
                    key={provider}
                    value={provider}
                    label={provider}
                    size="sm"
                    disabled={disabled}
                  />
                ))}
              </Group>
            </Checkbox.Group>
            {sharers.length > 0 && (
              <Text size="xs" c="dimmed">
                Also used by {sharers.join(', ')}. A provider stops running only when every section
                using this source excludes it.
              </Text>
            )}
            {noneLeft && (
              <Text size="xs" c="red">
                Leaving no provider selected will be rejected on save — this section would have no
                data.
              </Text>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
}
